/**
 * Flight details form: field presentation, event adapters, and departure confirmation.
 * The shared form/autosave hook owns timing and request ordering; graphql/ builds patches.
 * @packageDocumentation
 */
import React, {useRef, useState} from 'react';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    message,
    Modal,
    Row,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
} from 'antd';
import {FormComponentProps} from 'antd/lib/form/Form';
import {useMutation} from '@apollo/react-hooks';
import moment from 'moment';
import WithOrgData, {WithOrgDataProps} from '../../../WithOrgData';
import {LocationSelect} from '../../../form/select/LocationSelect';
import LocationOnshoreSelect from '../../../form/LocationOnshoreSelect';
import AircraftSelect from '../../../form/AircraftSelect';
import ContractSelect from '../../../form/ContractSelect';
import PilotSelect from '../../../form/PilotSelect';
import CstWarning from '../../../form/CstWarning';
import {FlightEditorFormField} from '../configuration';
import {routeFingerprint as fingerprintFlight} from '../route-edit';
import {reviewPlannedLegs} from '../auto-assign';
import {useAutosaveForm} from '../../../form/autosave';
import {normalizedValue} from './state/values';
import {saveDetails} from './graphql/save-details';
import {UPDATE_FLIGHT_FORM} from './graphql/mutation';
import './details-form.less';

const defaultRules = [
    {
        required: true,
        message: 'This field is required',
    },
];

/** Existing flight fields, permission state, and parent save notifications. */
export interface FlightDetailsFormProps extends FormComponentProps, WithOrgDataProps {
    data: any;
    disabled?: boolean;
    showWeatherDelay?: boolean;
    weatherDelayDisabled?: boolean;
    fields?: FlightEditorFormField[];
    onBusyChange?: (busy: boolean) => void;
    onSubmitted?: () => void | Promise<unknown>;
}

/** Restores a pilot selector's label/value shape, retaining an empty label when no name is saved. */
function pilotSelection(pilot?: {_id: string; name?: {lastName?: string; firstName?: string}}) {
    if (!pilot) return null;

    const label = pilot.name ? `${pilot.name.lastName || ''}, ${pilot.name.firstName || ''}` : '';
    return {key: pilot._id, label};
}

/** Adapts the existing Ant Design controls to field-specific autosaving. */
const FlightDetailsForm: React.FC<FlightDetailsFormProps> = (props) => {
    const {
        form: {getFieldValue},
        data,
        orgData,
        disabled: externallyDisabled,
        onSubmitted = () => null,
    } = props;

    const [reviewingDeparture, setReviewingDeparture] = useState(false);
    const [mutateDetails] = useMutation(UPDATE_FLIGHT_FORM);
    const disabled = externallyDisabled || reviewingDeparture;
    const currentFlightData = useRef(data);
    currentFlightData.current = data;

    const defaultFieldProps = {
        size: 'default' as 'small' | 'default' | 'large',
    };

    const formFields: any[] = [
        {
            label: 'Scheduled Flight Date',
            key: 'scheduledFlightDate',
            component: (
                <DatePicker
                    disabled
                    {...{
                        ...defaultFieldProps,
                        disabled: disabled,
                        style: {
                            width: '100%',
                        },
                    }}
                    disabledDate={(current) => current < moment().endOf('day')}
                    format="YYYY-MM-DD"
                />
            ),
            getValue: (data) => moment(data.scheduledFlightDate),
            options: {
                rules: defaultRules,
            },
        },
        {
            label: 'Est Departure Time',
            key: 'estDepartureTime',
            component: <TimePicker disabled={disabled} format="HH:mm" style={{width: '100%'}} />,
            getValue: (data) =>
                data.estDepartureTime ? moment(data.estDepartureTime, 'HH:mm') : undefined,
            displayEndText: (value) => <CstWarning />,
        },
        {
            label: 'Flight Designation',
            key: 'desig',
            component: <Input {...defaultFieldProps} disabled={disabled} />,
            options: {
                rules: defaultRules,
            },
        },
        {
            label: 'Controlling Heliport',
            key: 'lastKnownController',
            component: <LocationOnshoreSelect disabled labelInValue />,
            options: {
                rules: defaultRules,
            },
            getValue: (data) => {
                const lastKnownController = data['lastKnownController'];
                if (!lastKnownController) {
                    return null;
                }
                return {
                    key: lastKnownController._id,
                    label: lastKnownController.name,
                };
            },
        },
        {
            label: 'Departure',
            key: 'departureID',
            component: <LocationSelect disabled={disabled} labelInValue />,
            options: {
                rules: defaultRules,
            },
            getValue: (data) => {
                let departure = data['departureID'];
                if (!departure) {
                    if (data.legsArray && data.legsArray[0]) {
                        departure = data.legsArray[0].departureID;
                    } else {
                        return null;
                    }
                }
                return {
                    key: departure._id,
                    label: departure.name,
                };
            },
        },
        {
            label: 'Aircraft',
            key: 'aircraftID',
            component: <AircraftSelect disabled={disabled} labelInValue {...defaultFieldProps} />,
            options: {
                rules: defaultRules,
            },
            getValue: (data) => {
                const aircraft = data && data.aircraftID;
                if (!aircraft) return null;
                return {
                    key: aircraft._id,
                    label: aircraft.tailNum,
                };
            },
        },
        {
            label: 'Contract',
            key: 'contractID',
            component: (
                <ContractSelect
                    labelInValue
                    orgType={'customer'}
                    locationID={data['lastKnownController'] && data['lastKnownController']._id}
                    {...defaultFieldProps}
                    disabled={disabled}
                />
            ),
            options: {
                rules: defaultRules,
            },
            getValue: (data) => {
                const contract = data && data.contract;
                if (!contract) return null;
                return {
                    key: contract._id,
                    label: contract.name,
                };
            },
        },
        {
            label: 'Pilot',
            key: 'pilotID',
            component: <PilotSelect labelInValue {...defaultFieldProps} disabled={disabled} />,
            getValue: (flight) => pilotSelection(flight.pilot),
        },
        {
            label: 'Co-Pilot',
            key: 'copilotID',
            component: <PilotSelect labelInValue {...defaultFieldProps} disabled={disabled} />,
            getValue: (flight) => pilotSelection(flight.copilot),
        },
    ];

    if (props.showWeatherDelay && (!props.fields || props.fields.includes('weatherDelay'))) {
        formFields.push({
            label: 'Weather Delay',
            key: 'setWeatherDelay',
            configurationKey: 'weatherDelay',
            component: <Switch disabled={disabled || props.weatherDelayDisabled} />,
            getValue: (data) => Boolean(data.weatherDelayTs),
            options: {
                valuePropName: 'checked',
            },
            displayEndText: () =>
                data.weatherDelayTs
                    ? `${moment.tz(data.weatherDelayTs, 'America/Chicago').format('HH:mm')} CST`
                    : null,
        });
    }

    const visibleFormFields = formFields.filter(
        (field) =>
            !props.fields ||
            props.fields.includes((field.configurationKey || field.key) as FlightEditorFormField)
    );

    const autosave = useAutosaveForm({
        form: props.form,
        recordKey: data._id,
        disabled: Boolean(externallyDisabled),
        onBusyChange: props.onBusyChange,
        isEqual: (key, left, right) => normalizedValue(key, left) === normalizedValue(key, right),
        onSave: async (values) => {
            const flight = currentFlightData.current;
            const snapshot = fingerprintFlight(flight);
            const labels = new Map<string, string>([
                ...(flight.paxIDList || []).map(
                    (pax) =>
                        [`pax:${pax._id}`, `${pax.lastName || ''}, ${pax.firstName || ''}`] as [
                            string,
                            string
                        ]
                ),
                ...(flight.cgoIDList || []).map(
                    (cargo) => [`cgo:${cargo._id}`, cargo.name] as [string, string]
                ),
            ]);
            const result = await saveDetails({
                flight,
                values,
                tenant: {
                    tpID: orgData.transporter._id,
                    customerID: orgData.customer._id,
                    orgID: orgData.getActiveOrgID(),
                },
                isCurrent: () => fingerprintFlight(currentFlightData.current) === snapshot,
                onDepartureBusyChange: setReviewingDeparture,
                confirmDeparture: (departure) =>
                    new Promise<boolean>((resolve) => {
                        Modal.confirm({
                            title: 'Update passenger and cargo journeys?',
                            content: `Passengers and cargo using ${
                                flight.departureID?.name || flight.departureID?._id
                            } as their departure or destination will use ${
                                departure.name || departure._id
                            } instead. Continue to review the route and assignments?`,
                            okText: 'Continue to review',
                            cancelText: 'Cancel',
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        });
                    }),
                reviewDeparture: (legs) => reviewPlannedLegs(legs, true, labels),
                mutate: (variables) => mutateDetails({variables}),
            });
            if (result.saved) {
                try {
                    await onSubmitted();
                } catch {
                    // A refresh failure must not retry a write that already succeeded.
                    message.warning(
                        'Flight saved, but refreshing failed. Reload to see the latest data.'
                    );
                }
            }
            return result;
        },
    });

    const {getFieldDecorator} = autosave;
    const saveStatus = {
        idle: '',
        waiting: 'Unsaved changes',
        saving: 'Saving…',
        saved: 'Saved',
        invalid: 'Check highlighted fields',
        error: 'Changes not saved',
    }[autosave.status.phase];

    return (
        <Card className="flight-details-card" size="small">
            <div className="flight-details-save-status" role="status" aria-live="polite">
                <Typography.Text type={autosave.status.phase === 'error' ? 'danger' : 'secondary'}>
                    {saveStatus}
                </Typography.Text>
                {autosave.status.phase === 'error' && (
                    <Tooltip mouseEnterDelay={1} title={autosave.status.error}>
                        <Button
                            type="link"
                            disabled={Boolean(externallyDisabled)}
                            onClick={autosave.retry}
                        >
                            Retry
                        </Button>
                    </Tooltip>
                )}
            </div>
            <Form
                className="unified-flight-editor-form"
                layout="horizontal"
                onSubmit={(event) => event.preventDefault()}
            >
                <Row type="flex" gutter={12}>
                    {visibleFormFields.map((field, i) => (
                        <Col key={i} style={{flex: 1}}>
                            <Form.Item label={field.label}>
                                <Row type="flex" gutter={6}>
                                    <Col style={{flex: 1}}>
                                        {getFieldDecorator<Record<string, any>>(field.key, {
                                            ...field.options,
                                            initialValue: field.getValue
                                                ? field.getValue(data)
                                                : (data as unknown as Record<string, any>)[
                                                      field.key
                                                  ],
                                        })(field.component)}
                                    </Col>
                                    {field.displayEndText ? (
                                        <Col>{field.displayEndText(getFieldValue(field.key))}</Col>
                                    ) : null}
                                </Row>
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </Form>
        </Card>
    );
};

export default WithOrgData(Form.create<FlightDetailsFormProps>()(FlightDetailsForm));
